import {ChangeDetectorRef, Component, ComponentFactoryResolver, ViewEncapsulation,} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FieldRecord} from "../../../model/api/field-record";
import {FieldRequest} from "../../../model/api/field-request";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {CommonFieldService} from "../../../services/common/common-field.service";
import {CrudBaseComponent} from "../../../crystal-elements/crud/crud-base.component";
import {ExchangeDetail} from "../../../model/exchange/exchange-detail";
import {AccountDefinition} from "../../../model/customer/account-definition";
import {BrokerageTable} from "../../../model/customer/brokerage-table";
import {CustomerBrokerageTableService} from "../../../services/customer/customer-brokerage-table.service";
import TYPE = FormFieldConfig.TYPE;
import {CommRate} from "../../../model/billing/comm-rate";
import {Contract} from "../../../model/exchange/contract";

@Component({
  selector: 'app-brok-table',
  templateUrl: './brokerage-table.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
})
export class BrokerageTableComponent extends CrudBaseComponent<BrokerageTable> {

  //fields
  owners: FieldRecord<any>[]

  clearingHouses: FieldRecord<any>[]

  exchangeCodes: FieldRecord<ExchangeDetail>[]
  displayedExchangeCodes: FieldRecord<any>[]

  contractUnits: FieldRecord<any>[]
  brokerageRules: FieldRecord<any>[]
  currencies: FieldRecord<any>[]


  constructor(cdref: ChangeDetectorRef,
              customerBrokerageTableService: CustomerBrokerageTableService,
              commonFieldService: CommonFieldService,
              componentFactoryResolver: ComponentFactoryResolver,
              messageService: MessageService,
              confirmationService: ConfirmationService,
              translateService: TranslateService) {
    super(cdref, messageService, confirmationService, translateService, commonFieldService);
    this.crudService = customerBrokerageTableService;
    this.newRecord = new BrokerageTable();
  }

  protected initConfigs() {
    super.initConfigs();

    this.tableConfig = [
      {
        field: 'ownerCode',
      },
      {
        field: 'brokerageTableName',
      },
      {
        field: 'clearingHouse',
      },
      {
        field: 'exchangeCode',
        icon: (record: AccountDefinition) => {
          if (this.exchangeCodes) {
            const exchField = this.exchangeCodes.find(exchange => exchange.value.code === record.exchangeCode);
            if (exchField && exchField.value)
              return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
          }
          return 'fas fa-globe-europe';
        },
        mapId: record => record.exchangeCode ? record.exchangeCode : this.translateService.instant('page.account.form.defaultExchangeAll')
      },
      {
        field: 'ruleType',
      },
      {
        field: 'amountPerLot',
      },
      {
        field: 'currency',
        icon: record => record && record.currency ? 'cc-ico' : '',
        iconValue: record => record && record.currency && this.currencies ? this.currencies.find(value => value.id === record.currency).value : ''
      },
    ];

    this.formConfig = [
      {
        group: '',
        fields: [
          {
            field: 'ownerCode',
            optionValue: 'text',
            required: true,
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.owners,
            changed: event => {
              this.formRecord.ownerCode = event.value;
              this.formRecord.ownerId = Number(this.owners.find(value => value.text === event.value).id);
            }
          },
          {
            field: 'brokerageTableName',
            required: true,
            column: 4,
            type: TYPE.TEXT,
          }]
      },
      {
        group: 'detail',
        fields: [
          {
            field: 'clearingHouse',
            required: true,
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.clearingHouses,
            changed: event => {
              this.formRecord.clearingHouse = event.value;
              this.formRecord.exchangeCode = undefined;
              this.filterExchanges();
            }
          },
          {
            field: 'exchangeCode',
            icon: (id: string) => {
              if (this.exchangeCodes) {
                const exchField = this.exchangeCodes.find(exchange => exchange.value.code === id);
                if (exchField && exchField.value)
                  return exchField.value.country ? 'flag-icon flag-icon-' + exchField.value.country.toLowerCase() : '';
              }
              return 'fas fa-globe-europe';
            },
            filter: true,
            column: 4,
            type: TYPE.DROPDOWN,
            options: () => this.displayedExchangeCodes,
            changed: event => {
              this.formRecord.exchangeCode = event.value;
            }
          },
          {
            column: 4,
            type: TYPE.NONE
          },
          {
            field: 'contractCode',
            column: 6,
            type: TYPE.TEXT
          },
          {
            field: 'contractUnit',
            filter: true,
            column: 2,
            type: TYPE.DROPDOWN,
            options: () => this.contractUnits,
          },
          {
            field: 'allFutures',
            column: 2,
            type: TYPE.CHECKBOX
          },
          {
            field: 'allOptions',
            column: 2,
            type: TYPE.CHECKBOX
          },
          {
            field: 'ruleType',
            filter: true,
            column: 3,
            type: TYPE.DROPDOWN,
            options: () => this.brokerageRules,
          },
          {
            field: 'amountPerLot',
            column: 3,
            type: TYPE.DECIMAL,
            min: 0,
            max: 9999999999
          },
          {
            field: 'currency',
            column: 3,
            type: TYPE.DROPDOWN,
            filter: true,
            options: () => this.currencies,
            icon: id => 'cc-ico',
            iconValue: id => id && id.length > 0 && this.currencies && this.currencies.find(value => value.id === id) ?
              this.currencies.find(value => value.id === id).value : ''
          }]
      },
    ];
  }

  protected initData() {
    super.initData();

    this.settingsFormLoadingProgressValue = 0;
    this.brokerageRules = [];
    for (let key in CommRate.RuleType) {
      this.brokerageRules.push(new FieldRecord(key, key.toLowerCase()))
    }
    this.contractUnits = [];
    for (let key in Contract.ContractUnit) {
      this.contractUnits.push(new FieldRecord(Contract.ContractUnit[key], Contract.ContractUnit[key]))
    }
    this.commonFieldService.getOwners(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.owners = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 4;
    })
    this.commonFieldService.getClearingHouses(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.clearingHouses = this.getFields(fieldResult);
      this.filterExchanges();
      this.settingsFormLoadingProgressValue += 100 / 4;
    })
    this.commonFieldService.getExchangeCodes(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.exchangeCodes = this.getFields(fieldResult);
      this.filterExchanges();
      this.settingsFormLoadingProgressValue += 100 / 4;
    });
    this.commonFieldService.getCurrencies(FieldRequest.DEFAULT()).subscribe(fieldResult => {
      this.currencies = this.getFields(fieldResult);
      this.settingsFormLoadingProgressValue += 100 / 4;
    });
  }

  private filterExchanges() {
    let defaultAllExchange = FieldRecord.DEFAULT();
    defaultAllExchange.text = this.translateService.instant('page.account.form.defaultExchangeAll');
    defaultAllExchange.id = ExchangeDetail.ALL;
    if (this.exchangeCodes && this.formRecord) {
      this.displayedExchangeCodes = this.exchangeCodes.filter(field => this.formRecord.clearingHouse === field.value.clearingHouse);
      this.displayedExchangeCodes.unshift(defaultAllExchange);
    } else {
      this.displayedExchangeCodes = [defaultAllExchange];
    }
  }


  onFormShow() {
    this.filterExchanges();
  }
}
